import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import LanguageSelection from "../../components/ui/LanguageSelector";
import {
  HomeIcon,
  CalendarIcon,
  CalendarDaysIcon,
  CogIcon,
  UsersIcon,
  SparklesIcon,
  ChatBubbleLeftIcon,
  ChevronLeftIcon,
  Bars3Icon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";

const CompanyLayout = ({ children }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const profileRef = useRef(null);

  const getCompanyNameFromUser = () => {
    return (
      user?.company_name ||
      user?.companyName ||
      user?.company?.name ||
      user?.company?.company_name ||
      ""
    );
  };

  const displayCompanyName = useMemo(() => {
    const name = getCompanyNameFromUser();

    if (name && String(name).trim()) {
      return String(name).trim();
    }

    return "My Company";
  }, [user]);

  const companyInitials = useMemo(() => {
    return displayCompanyName.substring(0, 2).toUpperCase();
  }, [displayCompanyName]);

  useEffect(() => {
    console.log("COMPANY LAYOUT USER:", user);
    console.log("COMPANY NAME FROM USER:", getCompanyNameFromUser());
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      if (logout) {
        await logout();
      }

      navigate("/login");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const sidebarItems = [
    { name: t("company.sidebar.dashboard"), href: "/company", icon: HomeIcon },
    { name: t("company.sidebar.services"), href: "/company/services", icon: CogIcon },
    { name: t("company.sidebar.calendar"), href: "/company/calendar", icon: CalendarIcon },
    { name: t("company.sidebar.bookings"), href: "/company/bookings", icon: CalendarDaysIcon },
    { name: t("company.sidebar.employees"), href: "/company/employees", icon: UserGroupIcon },
    { name: t("company.sidebar.customers"), href: "/company/customers", icon: UsersIcon },
    { name: t("company.sidebar.chat"), href: "/company/chat", icon: ChatBubbleLeftIcon },
    { name: t("company.sidebar.ai_prompts"), href: "/company/ai-prompts", icon: SparklesIcon },
  ];

  const currentPageTitle =
    sidebarItems.find((item) => item.href === pathname)?.name || "Overview";

  return (
    <div className="theme-company flex h-screen">
      <aside
        className={`${
          isCollapsed ? "w-20" : "w-72"
        } bg-white border-r border-gray-100 flex flex-col shrink-0 transition-all duration-300 relative`}
      >
        <div
          className={`h-24 flex items-center shrink-0 ${
            isCollapsed ? "justify-center" : "justify-between px-8"
          }`}
        >
          {!isCollapsed && (
            <div className="flex items-center gap-3 min-w-0 animate-in fade-in duration-300">
              <span className="text-xl font-black text-slate-900 uppercase italic tracking-tighter truncate">
                Business<span className="text-[#FF5C35]">.</span>
              </span>
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsCollapsed((prev) => !prev)}
            className={`flex items-center justify-center transition-all duration-300 shrink-0 ${
              isCollapsed
                ? "p-2 hover:bg-slate-50 hover:bg-[#FF5C35] bg-[#0A1128] rounded-xl shadow-xl shadow-slate-200 text-gray-300 hover:text-gray-900"  
                : "p-2 hover:bg-slate-50 rounded-xl text-gray-300 hover:text-gray-900"
            }`}
          >
            {isCollapsed ? (
              <Bars3Icon className="h-5 w-5 stroke-[2.2]" />
            ) : (
              <ChevronLeftIcon className="h-5 w-5 stroke-[2.5]" />
            )}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto no-scrollbar overflow-x-hidden px-4 space-y-1.5">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.href;

            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center rounded-2xl transition-all duration-200 group ${
                  isCollapsed ? "justify-center h-12 w-12 mx-auto" : "px-4 py-3.5"
                } ${
                  isActive
                    ? "bg-gray-900 text-white shadow-lg shadow-gray-200"
                    : "text-slate-400 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <Icon
                  className={`h-5 w-5 shrink-0 ${
                    isCollapsed ? "" : "mr-4"
                  } ${
                    isActive
                      ? "stroke-[2.5]"
                      : "group-hover:text-slate-900 stroke-[2]"
                  }`}
                />

                {!isCollapsed && (
                  <span className="text-[11px] font-black uppercase tracking-[0.12em] truncate whitespace-nowrap animate-in fade-in duration-300">
                    {item.name}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="relative" ref={profileRef}>
          {isProfileOpen && (
            <div
              className={`absolute bottom-[85%] bg-white border border-gray-100 rounded-2xl shadow-2xl shadow-slate-200 p-2 z-50 animate-in slide-in-from-bottom-2 duration-200 ${
                isCollapsed ? "left-2 w-52" : "left-6 right-6"
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  setIsProfileOpen(false);
                  navigate("/");
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-xl transition-colors"
              >
                <GlobeAltIcon className="h-4 w-4" />

                <span className="text-[10px] font-black uppercase italic">
                  Перейти на главную
                </span>
              </button>

              <div className="my-1 h-px bg-gray-100" />

              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />

                <span className="text-[10px] font-black uppercase italic">
                  Выйти из панели
                </span>
              </button>
            </div>
          )}

          <div
            onClick={() => setIsProfileOpen((prev) => !prev)}
            className={`p-6 border-t border-gray-50 flex shrink-0 cursor-pointer hover:bg-slate-50 transition-colors ${
              isCollapsed ? "justify-center" : "px-8"
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-white font-black text-[10px] shrink-0 border-2 border-white shadow-md italic uppercase">
                {companyInitials}
              </div>

              {!isCollapsed && (
                <div className="overflow-hidden animate-in fade-in duration-300">
                  <p className="text-[10px] font-black text-gray-900 uppercase italic leading-none truncate">
                    {displayCompanyName}
                  </p>

                  <p className="text-[8px] text-[#FF5C35] font-bold uppercase tracking-widest mt-1">
                    Partner Panel
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-20 bg-white border-b border-gray-100 flex items-center px-10 justify-between">
          <h1 className="text-[11px] font-black text-gray-900 uppercase tracking-[0.2em] italic">
            {currentPageTitle}
          </h1>

          <div className="flex items-center">
            <LanguageSelection />
          </div>
        </header>

        <div className="flex-1 overflow-auto p-10 no-scrollbar bg-gray-50">
          {children}
        </div>
      </main>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default CompanyLayout;